import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ExternalLink, QrCode, X, Copy, Check, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Simple QR Code component using a free QR code API
const QRCodeDisplay = ({ url, size = 200 }) => {
  const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}&bgcolor=ffffff&color=2E7D32`;
  
  return (
    <div className="bg-white p-4 rounded-xl shadow-inner">
      <img
        src={qrApiUrl}
        alt="QR Code pour avis Google"
        className="w-full h-auto"
        style={{ maxWidth: size }}
      />
    </div>
  );
};

// Main Google Review Button Component
export const GoogleReviewButton = ({ 
  variant = 'default', // 'default', 'compact', 'banner'
  showQROption = true,
  className = ''
}) => {
  const [config, setConfig] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews/qr-data`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching review config:', error);
    }
  };

  const handleOpenReview = () => {
    if (config?.url) {
      window.open(config.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyLink = async () => {
    if (config?.url) {
      await navigator.clipboard.writeText(config.url);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!config) return null;

  // Compact version (for inline use)
  if (variant === 'compact') {
    return (
      <button
        onClick={handleOpenReview}
        className={`inline-flex items-center gap-2 text-[#2E7D32] hover:text-[#1B5E20] font-medium transition-colors ${className}`}
      >
        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
        Laisser un avis Google
        <ExternalLink className="w-3 h-3" />
      </button>
    );
  }

  // Banner version (for prominent display)
  if (variant === 'banner') {
    return (
      <div className={`bg-gradient-to-r from-[#E8F5E9] to-[#C8E6C9] rounded-2xl p-6 ${className}`}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md">
              <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Votre avis compte !</h3>
              <p className="text-sm text-gray-600">
                Partagez votre expérience sur Google
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {showQROption && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowQRModal(true)}
                className="border-[#2E7D32] text-[#2E7D32] hover:bg-[#E8F5E9]"
              >
                <QrCode className="w-4 h-4 mr-2" />
                QR Code
              </Button>
            )}
            <Button
              onClick={handleOpenReview}
              className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white shadow-lg"
            >
              <Star className="w-4 h-4 mr-2 fill-white" />
              Laisser un avis Google
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* QR Modal */}
        <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-center flex items-center justify-center gap-2">
                <QrCode className="w-5 h-5 text-[#2E7D32]" />
                Scannez pour laisser un avis
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-4">
              <QRCodeDisplay url={config.url} size={200} />
              <p className="text-sm text-gray-500 mt-4 text-center">
                Scannez ce QR code avec votre téléphone pour accéder directement à notre page Google
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="text-gray-600"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copié !' : 'Copier le lien'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Default button version
  return (
    <>
      <Button
        onClick={handleOpenReview}
        className={`bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-200 hover:border-[#2E7D32] shadow-sm ${className}`}
      >
        <div className="flex items-center gap-2">
          {/* Google logo colors */}
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span className="font-medium">Laisser un avis Google</span>
          <ExternalLink className="w-4 h-4 text-gray-400" />
        </div>
      </Button>

      {showQROption && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowQRModal(true)}
          className="ml-2 text-gray-500 hover:text-[#2E7D32]"
        >
          <QrCode className="w-5 h-5" />
        </Button>
      )}

      {/* QR Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center flex items-center justify-center gap-2">
              <QrCode className="w-5 h-5 text-[#2E7D32]" />
              Scannez pour laisser un avis
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            <QRCodeDisplay url={config.url} size={200} />
            <p className="text-sm text-gray-500 mt-4 text-center">
              Scannez ce QR code avec votre téléphone
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              className="mt-4"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copié !' : 'Copier le lien'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Post-Service Review Prompt Modal
export const ReviewPromptModal = ({
  isOpen,
  onClose,
  clientName,
  clientPhone,
  serviceType = 'general',
  agentName
}) => {
  const [config, setConfig] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchConfig();
    }
  }, [isOpen]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews/qr-data`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleOpenReview = () => {
    if (config?.url) {
      window.open(config.url, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };

  const handleSendWhatsApp = async () => {
    if (!clientPhone) {
      toast.error('Numéro de téléphone requis');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.post(
        `${API_URL}/reviews/send-whatsapp`,
        {
          client_phone: clientPhone,
          client_name: clientName,
          service_type: serviceType,
          agent_name: agentName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Open WhatsApp with the generated link
      window.open(response.data.whatsapp_url, '_blank');
      toast.success('WhatsApp ouvert avec le message pré-rempli');
      onClose();
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      toast.error('Erreur lors de la génération du message');
    } finally {
      setSending(false);
    }
  };

  const serviceLabels = {
    skincare: 'consultation SADASkinCare',
    baby: 'consultation SADAHealth Bébé',
    health: 'votre visite',
    general: 'votre visite'
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2E7D32]">
            <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
            Merci pour {serviceLabels[serviceType] || 'votre visite'} !
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <p className="text-gray-600">
            {clientName ? `${clientName}, v` : 'V'}otre avis nous aide à nous améliorer et à mieux vous servir.
          </p>

          {/* Option 1: Direct Google Review */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-medium text-gray-900 mb-3">Option 1 : Avis immédiat</h4>
            <Button
              onClick={handleOpenReview}
              className="w-full bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-200 justify-center"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Laisser un avis Google maintenant
            </Button>
          </div>

          {/* Option 2: WhatsApp Reminder */}
          {clientPhone && (
            <div className="bg-green-50 rounded-xl p-4">
              <h4 className="font-medium text-gray-900 mb-3">Option 2 : Rappel WhatsApp</h4>
              <p className="text-sm text-gray-600 mb-3">
                Envoyez un message WhatsApp au client avec le lien de l'avis
              </p>
              <Button
                onClick={handleSendWhatsApp}
                disabled={sending}
                className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white justify-center"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {sending ? 'Ouverture...' : `Envoyer via WhatsApp`}
              </Button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                → {clientPhone}
              </p>
            </div>
          )}

          {/* QR Code for in-store display */}
          {config?.url && (
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3 text-center">QR Code à montrer au client</h4>
              <div className="flex justify-center">
                <QRCodeDisplay url={config.url} size={150} />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose}>
            Plus tard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleReviewButton;
